import { useEffect, useMemo, useState } from 'react'
import debug from 'debug'
import { useDebounce } from 'hooks/useDebounce'
import { uniqBy } from 'lodash-es'
import MiniSearch from 'minisearch'

import { EventDocument } from '../components/SearchBar/types'

import { useThrottledValue } from './useThrottledValue'

const log = debug('app:search')
log.enabled = true

export const useMiniSearch = (_messages: EventDocument[], _search: string) => {
    const [miniSearch] = useState<MiniSearch>(
        () =>
            new MiniSearch<EventDocument>({
                idField: 'key',
                fields: ['body'],
                storeFields: ['key', 'channelId', 'body'],
            }),
    )
    const messages = useDebounce(_messages, 1000)
    const search = useThrottledValue(_search, 500)

    const filteredMessages = useMemo(
        () =>
            uniqBy(
                messages.map((m) => ({ ...m, id: m.key })),
                (e) => e.id,
            ),
        [messages],
    )

    const [termCount, setTermCount] = useState(0)

    useEffect(() => {
        const all = filteredMessages.filter((m) => m?.source)
        const existing = all.filter((m) => miniSearch.has(m.id))
        const missing = all.filter((m) => !miniSearch.has(m.id))
        miniSearch.addAll(missing)
        existing.forEach((m) => miniSearch.replace(m))
        setTermCount(miniSearch.termCount)
    }, [filteredMessages, miniSearch])

    const results = useMemo(() => {
        if (!termCount || search.length < 1) {
            return []
        }
        return miniSearch.search(search, {
            fuzzy: search.length < 3 ? 0 : 0.2,
            combineWith: 'AND',
            prefix: true,
        })
    }, [miniSearch, search, termCount])

    return results
}
